import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { buildFields, prettyLabel } from "./moduleFields";

interface ModuleConfigFormProps {
  moduleCode: string;
  initial: Record<string, unknown>;
  onSave: (config: Record<string, unknown>) => void | Promise<void>;
  saving?: boolean;
  error?: string | null;
}

/**
 * Hybrid config editor: a generic form driven by the shared catalog, plus
 * structured editors for the FAQ and Lead Capture modules.
 */
export function ModuleConfigForm({
  moduleCode,
  initial,
  onSave,
  saving,
  error,
}: ModuleConfigFormProps) {
  const { t } = useTranslation(["modules", "common"]);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setConfig(initial);
    setDirty(false);
  }, [initial]);

  function set(key: string, value: unknown) {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  }

  function pushItem(key: string) {
    set(key, [...((config[key] as unknown[]) ?? []), ""]);
    setDirty(true);
  }

  function setItem(key: string, index: number, value: string) {
    const items = [...((config[key] as unknown[]) ?? [])];
    items[index] = value;
    set(key, items);
    setDirty(true);
  }

  function removeItem(key: string, index: number) {
    const items = [...((config[key] as unknown[]) ?? [])];
    items.splice(index, 1);
    set(key, items);
    setDirty(true);
  }

  const fields = buildFields(moduleCode, config);

  const validationError =
    moduleCode === "faq"
      ? validateFaqConfig(config, (key: string) => t(key))
      : null;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (validationError) {
          return;
        }
        onSave(config);
      }}
    >
        {moduleCode === "faq" && (
          <FaqEditor config={config} set={set} removeItem={removeItem} />
        )}

        {moduleCode === "lead_capture" && (
        <LeadCaptureEditor
          config={config}
          set={set}
          pushItem={pushItem}
          setItem={setItem}
          removeItem={removeItem}
        />
      )}

      <div className="divide-y divide-slate-200 rounded-lg border border-slate-200">
        {fields.map((field) => (
          <div key={field.key} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:gap-4">
            <label className="text-sm font-medium text-slate-700 sm:w-56">
              {t(`modules:fields.${field.key}`, { defaultValue: field.label })}
            </label>
            <div className="flex-1">
              {field.type === "boolean" ? (
                <label className="inline-flex cursor-pointer items-center">
                  <input
                    type="checkbox"
                    checked={Boolean(config[field.key])}
                    onChange={(e) => set(field.key, e.target.checked)}
                  />
                  <span className="ml-2 text-sm text-slate-600">
                    {config[field.key] ? t("common:states.on") : t("common:states.off")}
                  </span>
                </label>
              ) : field.type === "number" ? (
                <input
                  type="number"
                  step="any"
                  className="input w-full"
                  value={String(config[field.key] ?? 0)}
                  onChange={(e) => set(field.key, Number(e.target.value))}
                />
              ) : field.type === "stringArray" ? (
                <div className="space-y-1">
                  {((config[field.key] as unknown[]) ?? []).map((item, i) => (
                    <div key={i} className="flex gap-2">
                      <input
                        className="input flex-1"
                        value={String(item)}
                        onChange={(e) => setItem(field.key, i, e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn-secondary px-2 text-sm"
                        onClick={() => removeItem(field.key, i)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  <button type="button" className="btn-secondary text-sm" onClick={() => pushItem(field.key)}>
                    {t("common:actions.add")}
                  </button>
                </div>
              ) : (
                <input
                  className="input w-full"
                  value={String(config[field.key] ?? "")}
                  onChange={(e) => set(field.key, e.target.value)}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {validationError && <p className="text-sm text-red-600">{validationError}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center justify-end gap-3">
        {dirty && <span className="text-xs text-slate-400">{t("common:states.unsavedChanges")}</span>}
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? t("common:states.saving") : t("common:actions.saveConfig")}
        </button>
      </div>
    </form>
  );
}

function validateFaqConfig(
  config: Record<string, unknown>,
  t: (key: string) => string,
): string | null {
  for (const key of ["welcome_message", "fallback_message", "farewell_message"]) {
    if (!String(config[key] ?? "").trim()) {
      return t("modules:requiredMessageError").replace("{{field}}", prettyLabel(key));
    }
  }
  const keywords = (config["farewell_keywords"] as unknown[]) ?? [];
  if (!keywords.some((k) => String(k).trim())) {
    return t("modules:requiredKeywordsError");
  }
  return null;
}

function FaqEditor({
  config,
  set,
  removeItem,
}: {
  config: Record<string, unknown>;
  set: (key: string, value: unknown) => void;
  removeItem: (key: string, index: number) => void;
}) {
  const { t } = useTranslation(["modules", "common"]);
  const items = (config["faq_items"] as unknown[]) ?? [];
  const keywords = (config["farewell_keywords"] as string[]) ?? [];

  interface FaqItemLike {
    question?: string;
    answer?: string;
    action?: string | null;
  }

  const actionLabels: Record<string, string> = {
    none: t("modules:actionNone"),
    lead_capture: t("modules:actionLeadCapture"),
    scheduling: t("modules:actionScheduling"),
    human_escalation: t("modules:actionHumanEscalation"),
  };

  return (
    <div className="space-y-4">
      {/* Bot messages (required) */}
      <div className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">
          {t("modules:botMessagesTitle")}
        </p>
        {(["welcome_message", "fallback_message", "farewell_message"] as const).map(
          (key) => (
            <div key={key} className="mb-3">
              <label className="mb-1 block text-xs font-medium text-slate-500">
                {t(`modules:fields.${key}`, { defaultValue: prettyLabel(key) })}
                <span className="ml-1 text-red-500">*</span>
              </label>
              <textarea
                className="input w-full"
                rows={2}
                value={String(config[key] ?? "")}
                onChange={(e) => set(key, e.target.value)}
              />
            </div>
          ),
        )}
        <div>
          <label className="mb-1 block text-xs font-medium text-slate-500">
            {t("modules:fields.farewell_keywords", {
              defaultValue: prettyLabel("farewell_keywords"),
            })}
            <span className="ml-1 text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {keywords.map((kw, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-sm">
                {kw}
                <button
                  type="button"
                  className="text-xs text-red-600"
                  onClick={() =>
                    set(
                      "farewell_keywords",
                      keywords.filter((_, idx) => idx !== i),
                    )
                  }
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
          <input
            className="input mt-2 w-full"
            placeholder={t("modules:addKeywordPlaceholder")}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                set("farewell_keywords", [...keywords, e.currentTarget.value.trim()]);
                e.currentTarget.value = "";
              }
            }}
          />
          <p className="mt-1 text-xs text-slate-400">{t("modules:keywordsHint")}</p>
        </div>
      </div>

      {/* FAQ items */}
      <div className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">{t("modules:faqItemsTitle")}</p>
        {items.length === 0 && <p className="text-sm text-slate-400">{t("modules:noFaqItems")}</p>}
        <div className="space-y-3">
          {(items as FaqItemLike[]).map((item, i) => (
            <div key={i} className="space-y-2 rounded-md border border-slate-200 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-slate-500">
                  {t("modules:itemNumber", { number: i + 1 })}
                </span>
                <button type="button" className="text-xs text-red-600" onClick={() => removeItem("faq_items", i)}>
                  {t("common:actions.remove")}
                </button>
              </div>
              <input
                className="input w-full"
                placeholder={t("modules:questionPlaceholder")}
                value={item?.question ?? ""}
                onChange={(e) => {
                  const next = [...(items as FaqItemLike[])];
                  next[i] = { ...item, question: e.target.value };
                  set("faq_items", next);
                }}
              />
              <textarea
                className="input w-full"
                placeholder={t("modules:answerPlaceholder")}
                rows={2}
                value={item?.answer ?? ""}
                onChange={(e) => {
                  const next = [...(items as FaqItemLike[])];
                  next[i] = { ...item, answer: e.target.value };
                  set("faq_items", next);
                }}
              />
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-slate-500">
                  {t("modules:actionLabel")}
                </label>
                <select
                  className="input flex-1"
                  value={item?.action ?? ""}
                  onChange={(e) => {
                    const next = [...(items as FaqItemLike[])];
                    next[i] = { ...item, action: e.target.value || null };
                    set("faq_items", next);
                  }}
                >
                  <option value="">{actionLabels.none}</option>
                  <option value="lead_capture">{actionLabels.lead_capture}</option>
                  <option value="scheduling">{actionLabels.scheduling}</option>
                  <option value="human_escalation">{actionLabels.human_escalation}</option>
                </select>
              </div>
              {item?.action ? (
                <p className="text-xs text-slate-400">{t("modules:actionHint")}</p>
              ) : null}
            </div>
          ))}
        </div>
        <button
          type="button"
          className="btn-secondary mt-3 text-sm"
          onClick={() => set("faq_items", [...(items as unknown[]), { question: "", answer: "", action: null }])}
        >
          {t("modules:addFaqItem")}
        </button>
      </div>
    </div>
  );
}

function LeadCaptureEditor({
  config,
  set,
  pushItem,
  setItem,
  removeItem,
}: {
  config: Record<string, unknown>;
  set: (key: string, value: unknown) => void;
  pushItem: (key: string) => void;
  setItem: (key: string, index: number, value: string) => void;
  removeItem: (key: string, index: number) => void;
}) {
  const { t } = useTranslation(["modules", "common"]);
  const fields = (config["custom_fields"] as unknown[]) ?? [];
  const required = (config["required_fields"] as unknown[]) ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">{t("modules:standardFieldsTitle")}</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {["collect_name", "collect_email", "collect_phone", "collect_company"].map((key) => (
            <label key={key} className="inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={Boolean(config[key])}
                onChange={(e) => set(key, e.target.checked)}
              />
              <span className="ml-2 text-sm text-slate-600">
                {t(`modules:fields.${key}`, { defaultValue: prettyLabel(key) })}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">{t("modules:requiredFieldsTitle")}</p>
        <div className="space-y-1">
          {required.map((item, i) => (
            <div key={i} className="flex gap-2">
              <input
                className="input flex-1"
                value={String(item)}
                onChange={(e) => setItem("required_fields", i, e.target.value)}
              />
              <button type="button" className="btn-secondary px-2 text-sm" onClick={() => removeItem("required_fields", i)}>
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="btn-secondary text-sm" onClick={() => pushItem("required_fields")}>
            {t("common:actions.add")}
          </button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <p className="mb-3 text-sm font-semibold text-slate-700">{t("modules:customFieldsTitle")}</p>
        {fields.length === 0 && <p className="text-sm text-slate-400">{t("modules:noCustomFields")}</p>}
        {(fields as { field_name?: string; label?: string }[]).map((item, i) => (
          <div key={i} className="mb-2 grid grid-cols-2 gap-2">
            <input
              className="input"
              placeholder={t("modules:fieldNamePlaceholder")}
              value={item?.field_name ?? ""}
              onChange={(e) => {
                const next = [...(fields as { field_name?: string; label?: string }[])];
                next[i] = { ...item, field_name: e.target.value };
                set("custom_fields", next);
              }}
            />
            <input
              className="input"
              placeholder={t("modules:labelPlaceholder")}
              value={item?.label ?? ""}
              onChange={(e) => {
                const next = [...(fields as { field_name?: string; label?: string }[])];
                next[i] = { ...item, label: e.target.value };
                set("custom_fields", next);
              }}
            />
          </div>
        ))}
        <button
          type="button"
          className="btn-secondary text-sm"
          onClick={() => set("custom_fields", [...(fields as unknown[]), { field_name: "", label: "" }])}
        >
          {t("modules:addCustomField")}
        </button>
      </div>
    </div>
  );
}
